import { memo } from 'react';
import type {
  WorkAiLevel,
  WorkBuiltLevel,
  WorkDesignedLevel,
  WorkJudgementLevel,
  WorkQuote,
  WorkReading as WorkReadingRecord,
  WorkSkipReason,
} from '../../../../../fsm/jobs';
import MaskedValue from '../masked-value';
import { taskName } from '../work-replay/format';
import styles from './index.module.css';

export interface WorkReadingProps {
  /** Rio's reading of the work task, written during the interview. Null or absent without one. */
  reading?: WorkReadingRecord | null;
  /**
   * The anonymised view. The server withholds the candidate's own words from it; this never draws any that arrive anyway,
   * and says they are hidden instead.
   */
  masked?: boolean;
}

/** How strongly a level is evidenced, never how good it is: the words carry the meaning, colour only steps up in emphasis. */
type Tone = 'strong' | 'mid' | 'plain' | 'none';

interface Level {
  label: string;
  meaning?: string;
  tone: Tone;
}

const BUILT: Record<WorkBuiltLevel, Level> = {
  all_pass: { label: 'All tests passing', tone: 'strong' },
  some_fail: { label: 'Some tests failing', tone: 'plain' },
  not_run: { label: 'Tests not run', tone: 'none' },
  changed_after_run: {
    label: 'Changed after the last run',
    meaning: 'submitted without running again',
    tone: 'plain',
  },
};

const DESIGNED: Record<WorkDesignedLevel, Level> = {
  most_covered: { label: 'Most points covered', tone: 'strong' },
  some_covered: { label: 'Some points covered', tone: 'mid' },
  few_covered: { label: 'Few points covered', tone: 'plain' },
  not_drawn: { label: 'Nothing drawn', tone: 'none' },
};

const AI: Record<WorkAiLevel, Level> = {
  careful: {
    label: 'Careful',
    meaning: 'checked what it gave',
    tone: 'strong',
  },
  mixed: { label: 'Mixed', meaning: 'checked some of it', tone: 'mid' },
  uncritical: {
    label: 'Took its output as it was',
    tone: 'plain',
  },
  guided: {
    label: 'Used the hints',
    meaning: 'written hints, never the answer',
    tone: 'mid',
  },
  did_not_use: { label: 'Did not use it', tone: 'none' },
};

const JUDGEMENT: Record<WorkJudgementLevel, Level> = {
  reasoned: {
    label: 'Reasoned',
    meaning: 'tied their choices to the task',
    tone: 'strong',
  },
  partial: { label: 'Partly reasoned', tone: 'mid' },
  not_enough_evidence: { label: 'Not enough evidence', tone: 'none' },
};

/** Why a task was left undone, in the words the hiring team reads. None of them is a mark against the candidate. */
const SKIPPED: Record<WorkSkipReason, string> = {
  declined: 'The candidate chose not to do the work task.',
  declined_in_chat:
    'The candidate told Rio they would rather not do the work task.',
  screen_not_shared:
    'The candidate did not share their screen, so the work task could not be recorded.',
  unsupported:
    "The candidate's device cannot share a screen, so the work task could not be done.",
};

const MAX_QUOTES = 3;

/** The lines as stored, trimmed; anything that is not a line is dropped. */
const linesOf = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [])
    .filter((line): line is string => typeof line === 'string')
    .map((line) => line.trim())
    .filter(Boolean);

/** The quotes as stored, trimmed, at most three. */
const quotesOf = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [])
    .filter(
      (item): item is WorkQuote =>
        !!item &&
        typeof item === 'object' &&
        typeof (item as WorkQuote).quote === 'string' &&
        (item as WorkQuote).quote.trim() !== '',
    )
    .slice(0, MAX_QUOTES)
    .map((item) => item.quote.trim());

interface Row {
  key: string;
  name: string;
  about: string;
  level?: Level;
  lines: string[];
  quotes: string[];
  /** Checklist points a drawing covered and left out. */
  covered: string[];
  missing: string[];
  /** Whether the candidate's own words are part of this reading (and so are hidden, not absent, in the anonymised view). */
  hasWords: boolean;
  /** Shown when there is no reading for this part. */
  empty: string;
}

const isSkipped = (reading: WorkReadingRecord): boolean =>
  !!reading.skipped && reading.skipped in SKIPPED;

const rowsOf = (reading: WorkReadingRecord): Row[] => {
  const canvas = !!reading.designedIt && !reading.builtIt;
  const done = canvas ? reading.designedIt : reading.builtIt;
  const doneLevel = canvas
    ? reading.designedIt && DESIGNED[reading.designedIt.level]
    : reading.builtIt && BUILT[reading.builtIt.level];
  const used = reading.usedAi;
  const usedLevel = used ? AI[used.level] : undefined;
  const guidedLines = [
    ...linesOf(reading.guided?.evidence),
    ...Object.values(reading.guided?.notes ?? {}).filter(
      (note): note is string => typeof note === 'string' && note.trim() !== '',
    ),
  ];
  const why = reading.judgement;
  const whyLevel = why ? JUDGEMENT[why.level] : undefined;

  return [
    {
      key: 'did',
      name: 'What they did',
      about: canvas
        ? "Designed it, against the task's checklist"
        : 'Built it, from the test runs the page reported',
      level: doneLevel || undefined,
      lines: linesOf(done?.evidence),
      quotes: [],
      covered: canvas ? linesOf(reading.designedIt?.covered) : [],
      missing: canvas ? linesOf(reading.designedIt?.missing) : [],
      hasWords: false,
      empty: 'No reading for this part.',
    },
    {
      key: 'assistant',
      name: 'How they used the assistant',
      about: 'How they asked it, and what they did with what came back',
      level: usedLevel,
      lines: [...linesOf(used?.evidence), ...guidedLines],
      quotes: quotesOf(used?.quotes),
      covered: [],
      missing: [],
      hasWords: !!used,
      empty: 'No reading for this part.',
    },
    {
      key: 'why',
      name: 'Why they chose it',
      about: "Their answers to Rio's questions about the work",
      level: whyLevel,
      lines: linesOf(why?.evidence),
      quotes: quotesOf(why?.quotes),
      covered: [],
      missing: [],
      hasWords: !!why,
      empty: 'Rio did not ask about this work, or there was nothing to read.',
    },
  ];
};

/** Whether there is a work reading to show: a task left undone with a known reason, or at least one part read. */
export const hasWorkReading = (reading?: WorkReadingRecord | null): boolean =>
  !!reading &&
  typeof reading === 'object' &&
  typeof reading.taskId === 'string' &&
  reading.taskId.trim() !== '' &&
  (isSkipped(reading) ||
    !!reading.builtIt ||
    !!reading.designedIt ||
    !!reading.usedAi);

const kindLabel = (reading: WorkReadingRecord): string | null => {
  if (reading.kind === 'whiteboard') return 'Whiteboard';
  if (reading.kind === 'design') return 'Design';
  return reading.languageLabel?.trim() || null;
};

const times = (count: number) => (count === 1 ? 'once' : `${count} times`);

/**
 * Rio's reading of the work task on the candidate page (Work Fluency phase 2): what the candidate did, how they used
 * the assistant, and why they chose what they did, each with the level Rio read and the plain lines behind it. Rio
 * writes the reading (avrio-rio agents/interview/workspace.py); this only lays it out. Evidence, not a score: none of it is
 * part of the fit score, and a task left undone is shown as that, never as a failure.
 *
 * In the anonymised view the candidate's own words are not shown at all: the server withholds them, and this draws the
 * place they would be as hidden.
 */
export const WorkReading = memo(
  ({ reading, masked = false }: WorkReadingProps) => {
    if (!reading || !hasWorkReading(reading)) return null;
    const name = taskName(reading.taskId);

    if (isSkipped(reading) && reading.skipped) {
      const runs = typeof reading.runs === 'number' ? reading.runs : 0;
      return (
        <div className={styles.skipped}>
          <p className={styles.skippedTitle}>Work task skipped</p>
          <p className={styles.skippedText}>{SKIPPED[reading.skipped]}</p>
          <p className={styles.skippedMeta}>
            {name}
            {reading.afterWork
              ? runs > 0
                ? ` · work had started, with the tests run ${times(runs)}`
                : ' · work had started'
              : ' · no work had started'}
          </p>
          <p className={styles.footnote}>Not part of the fit score.</p>
        </div>
      );
    }

    const meta = [name, kindLabel(reading)].filter(Boolean).join(' · ');
    return (
      <div className={styles.reading}>
        <p className={styles.meta}>{meta}</p>
        <ul className={styles.rows}>
          {rowsOf(reading).map((row) => (
            <li key={row.key} className={styles.row} aria-label={row.name}>
              <div className={styles.head}>
                <div className={styles.titles}>
                  <span className={styles.name}>{row.name}</span>
                  <span className={styles.about}>{row.about}</span>
                </div>
                {row.level && (
                  <span className={`${styles.pill} ${styles[row.level.tone]}`}>
                    {row.level.label}
                    {row.level.meaning && (
                      <span className={styles.meaning}>
                        : {row.level.meaning}
                      </span>
                    )}
                  </span>
                )}
              </div>
              {row.level ? (
                <>
                  {row.lines.length > 0 && (
                    <ul className={styles.lines}>
                      {row.lines.map((line, index) => (
                        <li key={`${index}-${line}`}>{line}</li>
                      ))}
                    </ul>
                  )}
                  {(row.covered.length > 0 || row.missing.length > 0) && (
                    <div className={styles.points}>
                      {row.covered.length > 0 && (
                        <div className={styles.pointGroup}>
                          <span className={styles.pointLabel}>Covered</span>
                          <ul className={styles.chips}>
                            {row.covered.map((point) => (
                              <li key={point} className={styles.chipMet}>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {row.missing.length > 0 && (
                        <div className={styles.pointGroup}>
                          <span className={styles.pointLabel}>Not covered</span>
                          <ul className={styles.chips}>
                            {row.missing.map((point) => (
                              <li key={point} className={styles.chip}>
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {!masked && row.quotes.length > 0 && (
                    <ul className={styles.quotes}>
                      {row.quotes.map((quote, index) => (
                        <li key={`${index}-${quote}`} className={styles.quote}>
                          <q className={styles.quoteText}>{quote}</q>
                        </li>
                      ))}
                    </ul>
                  )}
                  {masked && row.hasWords && (
                    <p className={styles.hiddenWords}>
                      <MaskedValue kind="text" compact />
                      <span className={styles.hiddenNote}>
                        Their own words are not shown in the anonymised view.
                      </span>
                    </p>
                  )}
                </>
              ) : (
                <p className={styles.empty}>{row.empty}</p>
              )}
            </li>
          ))}
        </ul>
        <p className={styles.footnote}>Not part of the fit score.</p>
      </div>
    );
  },
);

WorkReading.displayName = 'WorkReading';

export default WorkReading;
